import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography,
} from '@mui/material'
import { validateSignup } from '../../merchants/pricing-wizard/stages/Stage0_Signup'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { createPtlMerchant, errorText, searchAllMerchants, turnOnPtl } from '../ptlSetupApi'
import { Banner, SegmentTabs, merchantLabel } from './SetupUi'

const MODES = [
  { key: 'existing', label: 'Existing merchant' },
  { key: 'new', label: 'New merchant' },
]

/** Sign-up fields, the same names and checks as the D2C wizard sign-up (validateSignup), so both create the same merchant. */
/**
 * Lengths the merchant API enforces (MongoMerchant @Size), checked here so the message shows under the field instead of
 * a server line like "size must be between 3 and 75". The merchant name is the brand name, or the business name if blank.
 */
const lengthErrors = (f) => {
  const errs = {}
  const nameField = (f.brand || '').trim() ? 'brand' : 'business_name'
  const name = (f[nameField] || '').trim()
  if (name && (name.length < 3 || name.length > 75)) {
    errs[nameField] = nameField === 'brand' ? 'Brand name must be 3 to 75 characters.' : 'Business name must be 3 to 75 characters (or add a brand name).'
  }
  if ((f.email || '').length > 75) errs.email = 'Email can be at most 75 characters.'
  if ((f.city || '').length > 50) errs.city = 'City can be at most 50 characters.'
  if ((f.state || '').length > 50) errs.state = 'State can be at most 50 characters.'
  return errs
}

const FIELDS = [
  { name: 'business_name', label: 'Business name', required: true, help: 'Registered name, as per GST / PAN', sm: 6 },
  { name: 'brand', label: 'Brand name', help: 'At least 3 characters. Defaults to the business name', sm: 6 },
  { name: 'email', label: 'Email', required: true, type: 'email', help: 'Gets the merchant panel login', sm: 6 },
  { name: 'contact', label: 'Contact number', required: true, help: '10-digit mobile', inputMode: 'numeric', sm: 6 },
  { name: 'address', label: 'Billing / registered address', required: true, sm: 12 },
  { name: 'pincode', label: 'Pincode', required: true, inputMode: 'numeric', sm: 4 },
  { name: 'city', label: 'City', required: true, sm: 4 },
  { name: 'state', label: 'State', required: true, sm: 4 },
]

/**
 * Start a PTL merchant from PTL Merchant Setup: pick any live merchant (turning the PTL type on if it is off) or create a
 * new one with the PTL type on. Turning PTL on uses POST /api/ptl/setup/merchants/{id}/turn-on-ptl (PTL type only);
 * creating uses the shared merchant/create, as the D2C sign-up does. onDone(merchantId) opens its next step; isDone(merchantId) tells a PTL merchant that is already fully
 * set up, which onOpenUpdate(merchantId) sends to Merchant Update instead.
 */
export default function OnboardMerchantDialog({ open, onClose, onDone, isDone, onOpenUpdate }) {
  const [mode, setMode] = useState('existing')
  const [all, setAll] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [picked, setPicked] = useState(null)
  const [form, setForm] = useState({})
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!open || all) return
    searchAllMerchants().then(setAll).catch((e) => setLoadError(errorText(e, 'Could not load merchants')))
  }, [open, all])

  const options = useMemo(() => [...(all || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')), [all])

  const reset = () => { setPicked(null); setForm({}); setErrors({}); setSaveError(''); setBusy(false); setMode('existing') }
  const close = () => { if (busy) return; reset(); onClose() }
  const finish = (id) => { reset(); onDone(id) }

  const pickedIsPtl = !!picked?.isPTLMerchant
  const pickedDone = pickedIsPtl && isDone(picked.id)

  const continueExisting = () => {
    if (!picked) return
    if (pickedIsPtl) { finish(picked.id); return }
    setBusy(true); setSaveError('')
    turnOnPtl(picked.id).then(() => finish(picked.id))
      .catch((e) => { setBusy(false); setSaveError(errorText(e, 'Could not turn on the PTL merchant type')) })
  }

  const create = () => {
    const errs = { ...lengthErrors(form), ...validateSignup(form) }
    setErrors(errs)
    if (Object.keys(errs).length) return
    setBusy(true); setSaveError('')
    createPtlMerchant(form).then((m) => finish(m.id || m._id))
      .catch((e) => { setBusy(false); setSaveError(errorText(e, 'Could not create the merchant')) })
  }

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }))
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }))
  }

  const primary = mode === 'new'
    ? { label: 'Create PTL merchant', onClick: create, disabled: busy }
    : pickedDone
      ? { label: 'Open Merchant Update', onClick: () => { const id = picked.id; reset(); onOpenUpdate(id) }, disabled: busy }
      : { label: picked && !pickedIsPtl ? 'Turn on PTL and continue' : 'Continue', onClick: continueExisting, disabled: busy || !picked }

  return (
    <Dialog open={open} onClose={close} maxWidth='sm' fullWidth aria-labelledby='onboard-ptl-title'>
      <DialogTitle id='onboard-ptl-title' sx={{ ...TYPE.sectionTitle, fontSize: 16, pb: 0.5 }}>Onboard a PTL merchant</DialogTitle>
      <DialogContent>
        <SegmentTabs value={mode} onChange={(v) => { if (!busy) { setMode(v); setSaveError('') } }} items={MODES} ariaLabel='Existing or new merchant' />

        {mode === 'existing' ? (
          <Box>
            <Typography sx={{ ...TYPE.rowHelp, mb: 2 }}>
              Pick the merchant. If it does not have the PTL type yet, it is switched on here, as on the merchant details page. Its D2C set-up does not change.
            </Typography>
            {loadError && <Banner tone='danger'>{loadError}</Banner>}
            <Autocomplete autoHighlight options={options} loading={!all && !loadError} value={picked}
              onChange={(_, m) => { setPicked(m); setSaveError('') }}
              getOptionLabel={(m) => merchantLabel(m)} isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, m) => (
                <Box component='li' {...props} key={m.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <span>{merchantLabel(m)}</span>
                  <Box component='span' sx={{ ...SX.pill, flexShrink: 0, ...(m.isPTLMerchant ? { color: C.ok, bgcolor: C.okSoft } : { color: C.inkMuted, bgcolor: C.surfaceAlt }) }}>
                    {m.isPTLMerchant ? 'PTL' : m.isD2CMerchant ? 'D2C only' : 'No type'}
                  </Box>
                </Box>
              )}
              renderInput={(p) => (
                <TextField {...p} autoFocus label='Merchant' placeholder='Search name or company'
                  InputProps={{ ...p.InputProps, endAdornment: <>{!all && !loadError && <CircularProgress size={16} />}{p.InputProps.endAdornment}</> }} />
              )} />
            {picked && (
              <Box sx={{ mt: 2 }}>
                {pickedDone ? (
                  <Banner tone='info'>{picked.name} is already set up for PTL. Change its rate cards, TAT, allocation or zone map from Merchant Update.</Banner>
                ) : pickedIsPtl ? (
                  <Banner tone='info'>{picked.name} is a PTL merchant. Continue opens its next set-up step.</Banner>
                ) : (
                  <Banner tone='warn'>
                    Turn on PTL for {picked.name}? It then shows in PTL Merchant Setup, the PTL merchant filters and PTL billing.
                    {picked.isD2CMerchant ? ' It stays a D2C merchant as well.' : ''}
                  </Banner>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box component='form' noValidate onSubmit={(e) => { e.preventDefault(); create() }}>
            <Typography sx={{ ...TYPE.rowHelp, mb: 2 }}>
              Creates the merchant with the PTL type on, and its merchant panel login. KYC documents are added afterwards on the merchant details page.
            </Typography>
            <Grid container spacing={1.5}>
              {FIELDS.map((f) => (
                <Grid item xs={12} sm={f.sm} key={f.name}>
                  <TextField size='small' fullWidth name={f.name} label={f.label} required={f.required} type={f.type || 'text'}
                    value={form[f.name] || ''} onChange={(e) => setField(f.name, e.target.value)} disabled={busy}
                    error={!!errors[f.name]} helperText={errors[f.name] || f.help || ' '}
                    inputProps={f.inputMode ? { inputMode: f.inputMode } : undefined} />
                </Grid>
              ))}
            </Grid>
            {/* Lets Enter submit the form; the visible button is in the dialog footer. */}
            <button type='submit' hidden aria-hidden tabIndex={-1} />
          </Box>
        )}

        {saveError && <Box sx={{ mt: 2 }}><Banner tone='danger'>{saveError}</Banner></Box>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
        <Button variant='contained' onClick={primary.onClick} disabled={primary.disabled} sx={{ ...SX.cta, minHeight: 36 }}
          startIcon={busy ? <CircularProgress size={14} color='inherit' /> : null}>
          {primary.label}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
